import React from 'react'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { l } from '@manychat/manyui'

import { localStorage } from 'utils'
import { AiCommunicationsActions } from 'apps/aiCommunications/store'
import { WelcomeMessageSource } from 'apps/aiCommunications/store/types'
import { contentManagementActions } from 'apps/cms/store'
import { updateBuilderState } from 'common/builder/actions/updateBuilderState'
import { TriggerPreActivatedAlert } from 'common/builder/components/alerts/TriggerPreActivatedAlert/TriggerPreActivatedAlert'
import { DO_NOT_SHOW_TRIGGER_ALERT_STORAGE_KEY } from 'common/builder/constants/doNotShowTriggerAlertStorageKey'
import { FirstAutomationCreationLocalStorageIds } from 'common/builder/constants/FirstAutomationCreationLocalStorageIds'
import { alert } from 'common/core'
import {
  openFirstAutomationCreationModal,
  openFirstTikTokAutomationCreationModal,
} from 'common/core/actions/uiActions'
import { IThunkAction, ThunkExtra } from 'common/core/interfaces/actions'
import { getFirstTiktokAutomationCreation } from 'common/core/selectors/uiSelectors'
import { activateTriggerWithoutValidation } from 'common/flow/actions/flowTriggersActions'
import { getPreActivatableTriggers, getFlowTriggers } from 'common/flow/selectors/flowSelectors'
import { LightFlow, Flow } from 'shared/api/common/schemas/flow'
import { getAccountIdFromURL } from 'utils/accountId'
import { analyticsService } from 'utils/services/analytics'

const showDefaultAlert = (isFirstPublish: boolean) => {
  const message = isFirstPublish
    ? l.translate('Automation is Live')
    : l.translate('Live version is updated')

  return alert(message, 'success')
}

const openSidebarAndShowAlert = (
  dispatch: ThunkDispatch<RootState, ThunkExtra, AnyAction>,
  builderId: string,
  activationResult: Array<{ isActivated: boolean; triggerId: string }>,
) => {
  const allTriggersAreDisabled =
    activationResult.length === 0 || activationResult.every((trigger) => !trigger.isActivated)

  dispatch(
    updateBuilderState(builderId, {
      sidebarHidden: false,
      selectedNodes: {
        startingStep: true,
      },
    }),
  )

  alert(l.translate('Automation is live'), 'info', {
    icon: 'Info',
    content: allTriggersAreDisabled
      ? l.translate(
          'To start sending the Automation to your contacts, make sure to turn on Triggers within their settings.',
        )
      : l.translate(
          'Some of the Triggers are still disabled. Make sure to turn on them to trigger the following Automation.',
        ),
    shouldRemoveOnNavigate: true,
  })
}

export const tryToPreActivateTrigger = (
  flow: LightFlow | Flow,
  builderId: string,
  isFirstPublish: boolean,
): IThunkAction => {
  return async (dispatch, getState) => {
    const state = getState()
    const triggers = getFlowTriggers(state, flow.ns)

    const activatableTriggers = getPreActivatableTriggers(state, flow.ns)

    const showTriggersAlert = !localStorage.getItem(DO_NOT_SHOW_TRIGGER_ALERT_STORAGE_KEY)

    if (activatableTriggers.length === 0) {
      const allTriggersAreDisabled = triggers.every((trigger) => !trigger.enabled)

      if (triggers.length > 0 && allTriggersAreDisabled && showTriggersAlert) {
        openSidebarAndShowAlert(dispatch, builderId, [])
        return
      }

      if (state.ui.firstAutomationCreationModal?.isModalOpen) {
        return
      }
      return showDefaultAlert(isFirstPublish)
    }

    const activationResult = await Promise.all(
      activatableTriggers.map(async (trigger) => {
        const isActivated = await dispatch(activateTriggerWithoutValidation({ trigger: trigger }))

        return {
          isActivated,
          triggerId: trigger.triggerId,
        }
      }),
    )

    const allTriggersWereActivated = activationResult.every((result) => result.isActivated)

    analyticsService.sendEvent('PUBLISH_TRIGGERS_PREACTIVATION_RESULT', {
      activationResult,
    })

    if (allTriggersWereActivated) {
      return alert(l.translate('Automation is live'), 'info', {
        icon: 'CheckCircle',
        content: (
          <TriggerPreActivatedAlert activationResult={activationResult} builderId={builderId} />
        ),
        shouldRemoveOnNavigate: true,
      })
    }

    if (showTriggersAlert) {
      openSidebarAndShowAlert(dispatch, builderId, activationResult)
      return
    }
    if (state.ui.firstAutomationCreationModal?.isModalOpen) {
      return
    }
    showDefaultAlert(isFirstPublish)
  }
}

export const tryOpenFirstAutomationCreationModal = ({
  managerId,
  onOpenCongratsModal,
}: {
  managerId?: string
  onOpenCongratsModal?: () => void
} = {}): IThunkAction => {
  return async (dispatch, getState) => {
    const accountId = getAccountIdFromURL()
    if (!accountId) {
      return
    }

    const flowsMap = localStorage.getItem(FirstAutomationCreationLocalStorageIds.ACCOUNT_HAS_FLOWS)
    const flows = flowsMap ? JSON.parse(flowsMap) : {}
    const accountHasFlows = flows[accountId]

    const state = getState()

    const { isFirstSession: isTikTokFirstSession } = getFirstTiktokAutomationCreation(state)

    if (isTikTokFirstSession) {
      dispatch(openFirstTikTokAutomationCreationModal())
      return
    }

    const { payload: aiModalShown } = await dispatch(
      AiCommunicationsActions.tryOpenFirstAiAutomationCreationModal({
        managerId,
        accountId,
        source: WelcomeMessageSource.FLOW_BUILDER,
        checkNodes: true,
      }),
    )

    if (aiModalShown) {
      return
    }

    if (accountHasFlows) {
      dispatch(AiCommunicationsActions.tryOpenWelcomeMessageModal(false))
      return
    }

    dispatch(contentManagementActions.fetchFlows()).then((response) => {
      flows[accountId] = 'true'
      localStorage.setItem(
        FirstAutomationCreationLocalStorageIds.ACCOUNT_HAS_FLOWS,
        JSON.stringify(flows),
      )

      if (response.payload && 'flows' in response.payload && response.payload.flows.length === 1) {
        dispatch(openFirstAutomationCreationModal())
        onOpenCongratsModal?.()
      } else {
        dispatch(AiCommunicationsActions.tryOpenWelcomeMessageModal(false))
      }
    })
  }
}
